import { useNavigate } from 'react-router';
import { dateTime, qty } from '@/lib/format';
import { DataTable, Two, type Col } from './DataTable';
import { ExceptionBadge, shipmentHref, sumLines } from './ShipmentCard';
import type { ShipmentListItem } from './types';

/**
 * 发货批次台账表格（iPad S2 / 桌面）：列序照高保真稿；首列批次号吸左。
 * 「发货」列在稿里写「20 套 + 100 个」，但台账接口的 lines 不带 orderLine，认不出套装，
 * 这里如实按「个」累加（套装组件逐个计），套数进详情看。
 */
const COLS: Col<ShipmentListItem>[] = [
  { key: 'no', title: '发货批次', w: 168, fix: true, render: (r) => <b>{r.shipmentNo}</b> },
  { key: 'order', title: '业务订单', w: 160, render: (r) => r.order.orderNo },
  { key: 'cust', title: '客户 / 仓库', w: 168, render: (r) => <Two a={r.order.customer.name} b={r.warehouse?.name} /> },
  { key: 'lc', title: '物流公司', w: 112, render: (r) => r.logisticsCompany || <span className="em">—</span> },
  { key: 'tn', title: '物流单号', w: 160, render: (r) => r.trackingNo || <span className="em">—</span> },
  { key: 'lines', title: '产品行', w: 92, align: 'r', render: (r) => r.lines.length },
  { key: 'ship', title: '发货数量', w: 104, align: 'r', render: (r) => qty(sumLines(r).ship) },
  { key: 'loss', title: '破损 / 补耗', w: 100, align: 'r', render: (r) => { const v = sumLines(r).loss; return <span className={v ? 'neg' : 'em'}>{qty(v)}</span>; } },
  { key: 'out', title: '实际出库（个）', w: 120, align: 'r', render: (r) => <b>{qty(sumLines(r).out)}</b> },
  { key: 'exc', title: '异常', w: 84, align: 'c', render: (r) => (r.exceptions.length ? <ExceptionBadge list={r.exceptions} /> : <span className="em">—</span>) },
  { key: 'by', title: '出库人', w: 100, render: (r) => r.createdBy?.name || <span className="em">—</span> },
  { key: 'at', title: '发货时间', w: 152, render: (r) => dateTime(r.shippedAt) },
];

export const SHIPMENT_COLUMN_OPTIONS = COLS.map(({ key, title }) => ({ key, title: String(title) }));
export const DEFAULT_SHIPMENT_COLUMNS = COLS.filter((col) => col.key !== 'loss' && col.key !== 'out').map((col) => col.key);

const IPAD_COLUMN_WIDTHS: Record<string, number> = {
  no: 132, order: 122, cust: 90, lc: 72, tn: 110, lines: 38,
  ship: 63, out: 75, exc: 55, by: 50, at: 80,
};

export function ShipmentsTable({ items, columns = DEFAULT_SHIPMENT_COLUMNS, tablet = false }: { items: ShipmentListItem[]; columns?: string[]; tablet?: boolean }) {
  const nav = useNavigate();
  const visible = COLS.filter((col) => columns.includes(col.key)).map((col) => tablet ? { ...col, w: IPAD_COLUMN_WIDTHS[col.key] ?? col.w } : col);
  const designColumns = columns.length === DEFAULT_SHIPMENT_COLUMNS.length && DEFAULT_SHIPMENT_COLUMNS.every((key, index) => columns[index] === key);
  return <DataTable label="发货批次台账" cols={visible} rows={items} compact={tablet} fixed={!tablet && designColumns} onRow={(r) => nav(shipmentHref(r.shipmentNo), { state: { seed: r } })} />;
}
